import React from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, BedDouble, ListChecks, CalendarRange, CalendarCheck, CalendarClock, CalendarDays, Users, ClipboardList, BarChart3, Settings, UserCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { isAdmin, readCurrentUser } from '../lib/permissions';

const navItems = [
  { label: 'Overview', icon: LayoutDashboard, path: '/' },
  { label: 'Census', icon: BedDouble, path: '/census' },
  { label: 'Tasks', icon: ListChecks, path: '/tasks', adminOnly: true },
  { label: 'Schedule', icon: CalendarRange, path: '/schedule' },
  { label: 'My Availability', icon: CalendarCheck, path: '/my-availability' },
  { label: 'My Schedule', icon: CalendarDays, path: '/my-schedule' },
  { label: 'Availability', icon: CalendarClock, path: '/availability-admin', adminOnly: true },
  { label: 'Team', icon: Users, path: '/team', adminOnly: true },
  { label: 'Shift Notes', icon: ClipboardList, path: '/shift-notes', adminOnly: true },
  { label: 'Reports', icon: BarChart3, path: '/reports', adminOnly: true },
  { label: 'Profile', icon: UserCircle, path: '/profile', staffOnly: true },
  { label: 'Settings', icon: Settings, path: '/settings', adminOnly: true },
];

const Sidebar = ({ collapsed, onToggle }) => {
  const admin = isAdmin(readCurrentUser());
  const visibleItems = navItems.filter((item) => {
    if (item.adminOnly && !admin) return false;
    if (item.staffOnly && admin) return false;
    return true;
  });
  return (
    <motion.aside
      initial={{ width: collapsed ? 80 : 288 }}
      animate={{ width: collapsed ? 80 : 288 }}
      transition={{ duration: 0.22, ease: [0.2, 0.0, 0, 1] }}
      className="bg-muted/60 dark:bg-card border-r border-border/50 flex flex-col h-full"
      data-testid="sidebar"
    >
      <div className="p-6 flex items-center justify-between">
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-3"
          >
            {/* CareDesk logo — teal rounded square + white ECG pulse */}
            <svg
              width="40"
              height="40"
              viewBox="0 0 56 56"
              xmlns="http://www.w3.org/2000/svg"
              className="flex-shrink-0"
              aria-hidden="true"
            >
              <rect width="56" height="56" rx="14" fill="#1D9E75" />
              <polyline
                points="10,28 18,28 22,16 28,40 34,20 40,36 44,28 52,28"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {/* Wordmark */}
            <h1 className="text-[20px] font-medium tracking-tight leading-none">
              <span className="text-foreground">Care</span>
              <span style={{ color: '#1D9E75' }}>Desk</span>
            </h1>
          </motion.div>
        )}
        {collapsed && (
          <svg
            width="40"
            height="40"
            viewBox="0 0 56 56"
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto"
            aria-hidden="true"
          >
            <rect width="56" height="56" rx="14" fill="#1D9E75" />
            <polyline
              points="10,28 18,28 22,16 28,40 34,20 40,36 44,28 52,28"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>

      <nav className="flex-1 px-3 py-2 space-y-0.5" data-testid="sidebar-nav">
        {visibleItems.map((item, idx) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 h-9 px-3 rounded-lg text-sm font-medium transition-all duration-150 ${isActive
                ? 'bg-card text-primary shadow-soft'
                : 'text-foreground/70 hover:text-foreground hover:bg-card/60'
              }`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.02, duration: 0.22 }}
              >
                {item.label}
              </motion.span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-border/50">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-card transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-[18px] h-[18px]" /> : <ChevronLeft className="w-[18px] h-[18px]" />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </motion.aside>
  );
};

export default Sidebar;
